import { AssociatedDocuments, Decision, ModelType } from '@livv/models';
import { getDecisionLink } from '@livv/utils/helpers';
import { Box } from '@mui/material';
import { NextPage } from 'next';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import nookies from 'nookies';
import { useEffect, useRef, useState } from 'react';
import SameCaseTimeline from './SameCaseTimeline';
import SideContent from './SideContent';
import DocumentLayout from '@components/DocumentLayout';
import DocumentWithRightSidebar from '@components/DocumentLayout/DocumentWithRightSidebar';
import DocumentNotFound from '@components/DocumentNotFound';
import { InfoItemsListsContainer } from '@components/Layout';
import { InfoItemsListProps } from '@components/Layout/InfoItemsList';
import { filterInfoItemsList } from '@components/Layout/InfoItemsListsContainer';
import DecisionFullText from '@components/search/DecisionSearch/DecisionFullText';
import DecisionSearchProvider from '@components/search/DecisionSearch/DecisionSearchProvider';
import { getDecisionAssociatedDocuments } from '@functions';
import { APP_URL } from '@utils/consts/livv';
import { useFreemiumDialog } from '@utils/context/freemiumDialog';
import useUser from '@utils/context/user';
import { formatDateFromUnixTime, timestampToDate } from '@utils/date';
import isPdfExportAllowed from '@utils/entitlements/pdfExportPolicy';

interface TopInfoContainerProps {
    filteredPlaintiff: string[];
    filteredDefendant: string[];
    jury: {
        names: string[];
        position: string;
    }[];
}

const getTopContainerFilteredItemsListData = (
    { filteredPlaintiff, filteredDefendant, jury }: TopInfoContainerProps,
    t: Translate,
): InfoItemsListProps[] => {
    const itemsListData = [
        {
            fields: [
                {
                    label: t('plaintiff', { count: filteredPlaintiff.length }),
                    value: filteredPlaintiff,
                },
                {
                    label: t('defendant', { count: filteredDefendant.length }),
                    value: filteredDefendant,
                },
            ],
            title: t('parties'),
        },
        {
            fields: jury.map(({ names, position }) => ({
                label: t(
                    `juryPosition:${position}`,
                    { count: names.length },
                    { fallback: position },
                ),
                value: names,
            })),
            title: t('juridiction'),
        },
    ];

    return filterInfoItemsList(itemsListData);
};

const buildPageDescription = (
    {
        decisionType,
        juridictionName,
        number,
        performedAt,
    }: Pick<Decision, 'decisionType' | 'juridictionName' | 'number' | 'performedAt'>,
    lang: string,
    topInfos?: InfoItemsListProps,
): string => {
    const descNumber = number?.length ? number[0] : '';
    const descDate = formatDateFromUnixTime(performedAt.seconds, lang);

    // list of plaintiffs and defendants
    const rest: string = topInfos?.fields
        ? topInfos.fields.reduce((acc, { label, value }) => {
              if (!value?.length) {
                  return acc;
              }

              return `${acc}. ${label}: ${(value as string[]).join(', ')}`;
          }, '')
        : '';

    return `${descNumber}, ${juridictionName}, ${(
        decisionType as string
    ).toLowerCase()}, ${descDate}${rest}`;
};

export interface DecisionPageProps {
    analysisId?: string;
    associatedDocuments?: AssociatedDocuments;
    decision?: Decision;
    decisionId: string;
    fullText?: string;
}

const DecisionPage: NextPage<DecisionPageProps> = ({
    analysisId,
    associatedDocuments: initialAssociatedDocuments,
    decision,
    decisionId,
    fullText,
}) => {
    const { lang, t } = useTranslation('decision');
    const { openFreemiumDialog } = useFreemiumDialog();
    const skipRefresh = useRef(true);
    const [associatedDocuments, setAssociatedDocuments] = useState(initialAssociatedDocuments);
    const { info } = useUser();
    const showPrintButton = isPdfExportAllowed(ModelType.DECISION);

    // Refresh associated documents on user state change
    useEffect(() => {
        const refreshAssociatedDocuments = async () => {
            const token = info ? nookies.get().token : null;
            const { data } = await getDecisionAssociatedDocuments({
                decisionId,
                token,
            });
            if (data.status === 'ok') {
                setAssociatedDocuments(data.associatedDocuments);
            } else {
                setAssociatedDocuments(undefined);
            }
        };

        // Skip refresh on initial load (because data is already in sync with user state)
        if (skipRefresh.current) {
            skipRefresh.current = false;
        } else {
            refreshAssociatedDocuments();
        }
    }, [decisionId, info]);

    if (!decision) return <DocumentNotFound />;

    const {
        biblio,
        city,
        decisionType,
        defendant,
        juridictionName,
        jury,
        number,
        performedAt,
        plaintiff,
        result,
        sameCase,
        summary,
        title,
    } = decision;

    const filteredPlaintiff = plaintiff.filter((name) => name);
    const filteredDefendant = defendant.filter((name) => name);

    const filteredItems = getTopContainerFilteredItemsListData(
        { filteredDefendant, filteredPlaintiff, jury },
        t,
    );
    const topInfoContainer = filteredItems?.length !== 0 && (
        <InfoItemsListsContainer itemsListsData={filteredItems} listIsFiltered />
    );

    const sidebarContent = (
        <SideContent
            associatedDocuments={associatedDocuments}
            bibliography={biblio}
            decisionYear={timestampToDate(performedAt.seconds).getFullYear()}
            focusedAnalysisId={analysisId}
        />
    );

    const decisionTypeStr = Array.isArray(decisionType) ? decisionType.join(', ') : decisionType;

    const headDescription = buildPageDescription(
        { decisionType: decisionTypeStr, juridictionName, number, performedAt },
        lang,
        filteredItems[0],
    );

    return (
        <DocumentLayout
            analysesWithPremiumStatus={
                associatedDocuments?.[0].status === 'ok' ? associatedDocuments[0] : undefined
            }
            canonicalUrl={`https://${APP_URL}${getDecisionLink(decisionId)}`}
            customPrintHandler={openFreemiumDialog}
            documentId={decisionId}
            headTitle={`${title} - ${juridictionName}`}
            maxContentWidth="lg"
            modelType={ModelType.DECISION}
            pageDescription={`${t('modelType:PrefixedSingular.Decision')} ${headDescription}`}
            showPrintButton={showPrintButton}
            subtitle={city || juridictionName}
            subtitleElements={[decisionTypeStr, summary, result ?? '']}
            tags={[]}
            title={title}
            topInfosContainer={topInfoContainer}
            printable
        >
            <DecisionSearchProvider key={analysisId ?? 'decision'} focusedAnalysisId={analysisId}>
                <Box sx={{ maxWidth: { md: 'none', xs: '92vw' }, mt: 2 }}>
                    <Box>
                        <SameCaseTimeline
                            currentDecision={{ id: decisionId, ...decision }}
                            sameCase={sameCase}
                        />
                    </Box>
                    <DocumentWithRightSidebar
                        sidebarContent={sidebarContent}
                        sidebarTestId="decision-side-content"
                    >
                        <DecisionFullText decisionId={decisionId} fullText={fullText} />
                    </DocumentWithRightSidebar>
                </Box>
            </DecisionSearchProvider>
        </DocumentLayout>
    );
};

export default DecisionPage;
